import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { IModalAttachment } from '@entities/attachment/model/IModalAttachment.interface';
import { FieldErrors } from 'entities/modal/model/types/modalFieldErrors';
import { createCustomTask } from 'entities/task/api/createCustomTask';

import { ModalAddNewTaskView } from './ModalAddNewTaskView';
import { useModalAddNewTask } from './useModalAddNewTask';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getUpdatedFieldErrors } from '../../lib/getUpdatedFieldErrors';
import {
    changeIsCompleted,
    changeModalDescription,
    changeModalTitle,
    resetModalData,
} from '../../model/slice/addModalSlice';
import { TAPIError } from '../../model/types/apiError.type';

interface ModalAddNewTaskProps {
    portalFileOverlays?: boolean;
}

export const ModalAddNewTask = ({ portalFileOverlays = false }: ModalAddNewTaskProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [fileIdToPreview, setFileIdToPreview] = useState<string | null>(null);
    const [fileIdToDeleteFromList, setFileIdToDeleteFromList] = useState<string | null>(null);
    const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);

    const { modalMode, onHideModal, previewEnable, setPreviewEnable } = useModalAddNewTask();
    const modalState = useAppSelector((state) => state.addModalReducer);
    const { isModalLoading } = useAppSelector((state) => state.tasksReducer);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        title: { isError: false, errorMessage: null },
        description: { isError: false, errorMessage: null },
        objectOrEstimate: { isError: false, errorMessage: null },
        executor: { isError: false, errorMessage: null },
        tags: { isError: false, errorMessage: null },
        date: { isError: false, errorMessage: null },
    });
    const [attachments, setAttachments] = useState<IModalAttachment[]>([]);

    const [triggerAnimationError, setTriggerAnimationError] = useState(false);
    const [isCheckedRadio, setIsCheckedRadio] = useState(false);

    const deleteAttachment = (fileId: string | undefined) => {
        if (!fileId) return;
        setAttachments((prev) => prev.filter((attachment) => attachment.id !== fileId));
        setFileIdToDeleteFromList(null);
        setDeleteConfirmModal(false);
    };

    const clearFieldErrors = () => {
        setFieldErrors({
            title: { isError: false, errorMessage: null },
            description: { isError: false, errorMessage: null },
            objectOrEstimate: { isError: false, errorMessage: null },
            executor: { isError: false, errorMessage: null },
            tags: { isError: false, errorMessage: null },
            date: { isError: false, errorMessage: null },
        });
    };

    const onHidePrevModal = useCallback(() => {
        setPreviewEnable(false);
        setFileIdToPreview(null);
    }, [setPreviewEnable]);
    const onEnablePreviewModal = useCallback((enablePreview: boolean, fileId: string) => {
        setPreviewEnable(enablePreview);
        setFileIdToPreview(fileId);
    }, [setPreviewEnable]);

    const handleRadioBtn = () => {
        if (modalState.title.length === 0) {
            return;
        }
        setIsCheckedRadio(!isCheckedRadio);
        dispatch(changeIsCompleted(!isCheckedRadio));
    };

    const onChangeModalTitle = (title: string) => {
        if (fieldErrors.title.isError) {
            setFieldErrors((prev) => ({
                ...prev,
                title: { isError: false, errorMessage: null },
            }));
        }
        dispatch(changeModalTitle(title));
    };
    const onChangeModalDescription = (description: string) => {
        if (fieldErrors.description.isError) {
            setFieldErrors((prev) => ({
                ...prev,
                description: { isError: false, errorMessage: null },
            }));
        }
        dispatch(changeModalDescription(description));
    };

    const onSubmit = async () => {
        if (modalState.title.trim().length === 0) {
            setFieldErrors((prev) => ({
                ...prev,
                title: {
                    isError: true,
                    errorMessage: t('ModalAddNewTask.onSubmit.errorMessage'),
                },
            }));
            setTriggerAnimationError(true);
            return;
        }

        if (
            fieldErrors.date.isError ||
            fieldErrors.tags.isError ||
            fieldErrors.title.isError ||
            fieldErrors.description.isError ||
            fieldErrors.executor.isError ||
            fieldErrors.objectOrEstimate.isError
        ) {
            console.error(t('ModalAddNewTask.onSubmit.errorMessage.console_error'));
            return;
        }

        try {
            clearFieldErrors();
            await dispatch(createCustomTask({ ...modalState, attachments })).unwrap();
            onHideModal();
            dispatch(resetModalData());
        } catch (error) {
            if (error?.errors) {
                const apiErrors = error.errors as TAPIError[];
                const updatedFieldErrors = getUpdatedFieldErrors(fieldErrors, apiErrors);
                setFieldErrors(updatedFieldErrors);
            } else {
                console.error(t('ModalAddNewTask.onSubmit.unknownError'), error);
            }
        }
    };

    return (
        <ModalAddNewTaskView
            portalFileOverlays={portalFileOverlays}
            attachments={attachments}
            deleteConfirmModal={deleteConfirmModal}
            fieldErrors={fieldErrors}
            fileIdToDeleteFromList={fileIdToDeleteFromList}
            fileIdToPreview={fileIdToPreview}
            isCheckedRadio={isCheckedRadio}
            isModalLoading={isModalLoading}
            modalMode={modalMode}
            modalState={modalState}
            previewEnable={previewEnable}
            triggerAnimationError={triggerAnimationError}
            deleteAttachment={deleteAttachment}
            handleRadioBtn={handleRadioBtn}
            onChangeModalDescription={onChangeModalDescription}
            onChangeModalTitle={onChangeModalTitle}
            onEnablePreviewModal={onEnablePreviewModal}
            onHideModal={onHideModal}
            onHidePrevModal={onHidePrevModal}
            onSubmit={onSubmit}
            setAttachments={setAttachments}
            setDeleteConfirmModal={setDeleteConfirmModal}
            setFieldErrors={setFieldErrors}
            setFileIdToDeleteFromList={setFileIdToDeleteFromList}
            setTriggerAnimationError={setTriggerAnimationError}
        />
    );
};
